import { useTemplate } from './template.store';
import { TemplatePersistence } from './template.persistence';

import { type MessageTemplate } from './template.type';
import MeService from '@/core/shared/me/me.service';
import ConversationIndexService from '../conversation-index/conversation-index.service';
import { nextTick } from 'vue';

export const PLACEHOLDER_REGEX = /(\[['A-Za-z\s]*\])/g;

/**
 * Outcome of a template mutation. By the time this resolves the mutation itself has
 * already succeeded; `refreshError` only reports that the follow-up list refresh failed.
 * Callers must never present that as a failed save — doing so invites the recruiter to
 * repeat a create that already went through.
 */
export interface TemplateMutationResult {
  refreshError?: unknown;
}

export default class TemplateService {
  private store = useTemplate();
  private persistence = new TemplatePersistence();

  private meService = new MeService();
  private conversationIndexService = new ConversationIndexService();

  /** Drops the persisted template cache. Called when the signed-in user changes. */
  public clear(): void {
    this.store.clear();
  }

  public get templates(): MessageTemplate[] {
    return this.store.templates;
  }

  public set templates(templates: MessageTemplate[]) {
    this.store.templates = templates;
  }

  public get selectedTemplate(): MessageTemplate | null {
    return this.store.selectedTemplate;
  }

  public set selectedTemplate(selectedTemplate: MessageTemplate | null) {
    this.store.selectedTemplate = selectedTemplate;
  }

  public get showTemplateModal(): boolean {
    return this.store.showTemplateModal;
  }

  public set showTemplateModal(showTemplateModal: boolean) {
    this.store.showTemplateModal = showTemplateModal;
  }

  /**
   * Inserts a chip at the current cursor position in a text field: Since this insert in now being used
   * in multiple places lets reuse the logic
   * @param chipText - The chip text to insert
   * @param textAreaEl - The textarea element reference
   * @param currentText - The current text in the textarea
   * @param updateCallback - Callback function to update the text value
   * @param maxLength - Optional maximum length constraint
   * @param onMaxLengthExceeded - Optional callback for when the max length is exceeded
   * @returns The updated text with the chip inserted
   */
  public insertChip(
    chipText: string,
    textAreaEl: HTMLTextAreaElement | null,
    currentText: string,
    updateCallback: (newText: string) => void,
    maxLength?: number,
    onMaxLengthExceeded?: () => void,
  ): void {
    if (!chipText.length || !textAreaEl) return;

    const sentence = currentText || '';
    const len = sentence.length;

    // Check if inserting the chip would exceed the maximum length
    if (maxLength && len + chipText.length > maxLength) {
      if (onMaxLengthExceeded) {
        onMaxLengthExceeded();
      }
      textAreaEl.focus();
      return;
    }

    const startSelection = textAreaEl.selectionStart || 0;
    const endSelection = textAreaEl.selectionEnd || startSelection;

    const textBeforeChip = sentence.substring(0, startSelection);
    const textAfterChip = sentence.substring(endSelection, len);

    const newText = textBeforeChip + chipText + textAfterChip;
    updateCallback(newText);

    nextTick().then(() => {
      if (textAreaEl) {
        textAreaEl.selectionStart = textAreaEl.selectionEnd = startSelection + chipText.length;
        textAreaEl.focus();
      }
    });
  }

  public replaceTemplatePlaceholders(template: string): string {
    return template.replace(PLACEHOLDER_REGEX, (match) => match);
  }

  /**
   * Maps template placeholders to their actual values
   * @param template - The template string with placeholders
   * @param addHtmlTags - Whether to add HTML tags to the placeholder values
   * @param proFullName - Optional pro full name for bulk messaging
   * @param jobDisplayTitle - Optional job display title for bulk messaging
   * @param proFirstName - Optional pro first name; defaults to the first word of the full name
   */
  public mapTemplatePlaceholders(
    template: string,
    addHtmlTags: boolean = false,
    proFullName?: string,
    jobDisplayTitle?: string,
    proFirstName?: string,
  ): string {
    // Nullish, never `||`: a caller that deliberately passes an empty name — a candidate
    // with no name on record — must render nothing. Treating '' as "not provided" would
    // substitute the name of whichever conversation happens to be selected elsewhere in
    // the session, i.e. send one candidate another candidate's name. Same for the title.
    const proFullNameValue =
      proFullName ?? this.conversationIndexService.selectedConversationIndex?.proName ?? '';

    const jobTitleValue =
      jobDisplayTitle ??
      this.conversationIndexService.selectedConversationIndex?.primaryApplicationIndex
        ?.jobDisplayTitle ??
      '';

    // Conversation surfaces only carry a single `proName`, so the first word of the full
    // name stands in for them; callers holding the separate fields pass their own.
    const proFirstNameValue = proFirstName ?? proFullNameValue.split(' ')[0];

    let placeHolderMappings = [
      {
        regex: /\[(Company Name)\]/gi,
        text: '[Company Name]',
        id: this.meService.employer?.name || '',
      },
      {
        regex: /\[(Pro FullName)\]/gi,
        text: '[Pro FullName]',
        id: proFullNameValue,
      },
      {
        regex: /\[(My Name)\]/gi,
        text: '[My Name]',
        id: [this.meService.userProfile?.nameFirst, this.meService.userProfile?.nameLast]
          .filter(Boolean)
          .join(' '),
      },
      {
        regex: /\[(Pro FirstName)\]/gi,
        text: '[Pro FirstName]',
        id: proFirstNameValue,
      },
      {
        regex: /\[(Job Title)\]/gi,
        text: '[Job Title]',
        id: jobTitleValue,
      },
    ];
    if (addHtmlTags) {
      placeHolderMappings = placeHolderMappings.map((mapping) => {
        return {
          ...mapping,
          id: `<span class="bg-inform-100 text-inform-800 py-0.5 px-px">${mapping.id}</span>`,
        };
      });
    }
    return placeHolderMappings.reduce((acc, mapping) => {
      return acc.replace(mapping.regex, mapping.id);
    }, template);
  }

  public mapTemplatePlaceholdersForBulkMessaging(template: string): string {
    return template.replace(PLACEHOLDER_REGEX, (match) => {
      return `<span class="bg-inform-100 text-inform-800 py-0.5 px-px">${match}</span>`;
    });
  }

  public async fetchTemplates(): Promise<void> {
    const userProfileId = this.meService.userProfile?.id;
    if (userProfileId) {
      this.templates = await this.persistence.getTemplates(userProfileId.toString());
    }
  }

  public async createTemplate(
    template: Pick<MessageTemplate, 'title' | 'message'>,
  ): Promise<TemplateMutationResult> {
    const created = await this.persistence.createTemplate(template);

    // Reflect the new template locally so the list is right even if the refresh below
    // fails. Server ordering (`isFavorite DESC, updateTs DESC`) returns with the next
    // successful fetch.
    if (created?.id) {
      this.templates = [...this.templates, created];
    }

    return this.refreshTemplates();
  }

  public async updateTemplate(
    template: Pick<MessageTemplate, 'id' | 'title' | 'message'>,
  ): Promise<TemplateMutationResult> {
    await this.persistence.updateTemplate(template);

    this.templates = this.templates.map((existing) =>
      existing.id === template.id
        ? { ...existing, title: template.title, message: template.message }
        : existing,
    );

    return this.refreshTemplates();
  }

  public async deleteTemplate(templateId: number): Promise<TemplateMutationResult> {
    await this.persistence.deleteTemplate(templateId);

    this.templates = this.templates.filter((existing) => existing.id !== templateId);

    return this.refreshTemplates();
  }

  public async restoreTemplate(templateId: number): Promise<TemplateMutationResult> {
    await this.persistence.restoreTemplate(templateId);

    // Nothing to re-add locally: the status endpoint returns no template body, so the
    // restored template reappears with the refresh below.
    return this.refreshTemplates();
  }

  /**
   * Refreshes the cached list after a mutation that already succeeded. A refresh
   * failure is returned rather than thrown, so a completed create/update/delete is
   * never reported to the user as a failure.
   */
  private async refreshTemplates(): Promise<TemplateMutationResult> {
    try {
      await this.fetchTemplates();
      return {};
    } catch (refreshError) {
      return { refreshError };
    }
  }
}
